import { useNavigate, useParams } from 'react-router';

import { Typography, Card, styled, Grid, Button } from '@mui/material';

import { ConfirmDialog, DeactivateUserDialog } from '../../../components/dialogs';
import { useDenyReportedUser, useGetReportedUserById, useReinstateReportedUser } from '../../../lib/report/hooks';
import { useDeactivateUser } from '../../../lib/user/hooks';

function ReportedUserEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const reportedUserId = id ?? '';
  const getReportedUserById = useGetReportedUserById(reportedUserId);
  const getReportedUsers = getReportedUserById.data;
  let ready = false;

  ready = !getReportedUserById.isLoading;

  const deactivateUser = useDeactivateUser();
  const reinstateUser = useReinstateReportedUser();
  const denyReportedUser = useDenyReportedUser();

  const handleDeactivate = async (id: string, hideArticles: boolean) => {
    await deactivateUser.mutateAsync({ userId: id, hideArticles });
    navigate(-1);
  };

  const handleReinstate = async (id: string) => {
    await reinstateUser.mutateAsync(id);
    navigate(-1);
  };

  const handleDenyReport = async (id: string) => {
    await denyReportedUser.mutateAsync(id);
    navigate(-1);
  };

  const reportDenied = Boolean(
    getReportedUsers?.[0]?.reportDenied ||
      getReportedUsers?.[0]?.denied ||
      getReportedUsers?.[0]?.reportStatus === 'Report Denied' ||
      getReportedUsers?.[0]?.status === 'Report Denied',
  );

  return (
    <>
      {ready && getReportedUsers && (
        <Container>
          <TopBar>
            <Typography variant="h5">
              Reported User for:{' '}
              {getReportedUsers[0]?.user.firstName && getReportedUsers[0]?.user.lastName
                ? `${getReportedUsers[0]?.user.firstName} ${getReportedUsers[0]?.user.lastName}`
                : getReportedUsers[0]?.user.username}{' '}
            </Typography>
          </TopBar>

          <StyledCard>
            <StyledMainGrid container rowSpacing={4} mb={4}>
              {getReportedUsers.map((reportedUser: any, index: number) => {
                const reporter =
                  reportedUser?.reporter.firstName && reportedUser?.reporter.lastName
                    ? `${reportedUser?.reporter.firstName} ${reportedUser?.reporter.lastName}`
                    : reportedUser?.reporter.username;
                return (
                  <StyledGrid container rowSpacing={4} mb={4} key={index}>
                    <Grid item xs={12}>
                      <Typography>Reported Date: {new Date(reportedUser?.created).toLocaleDateString()}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>Reporter Username: {reporter} </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>Reason: {reportedUser?.reason}</Typography>
                    </Grid>
                  </StyledGrid>
                );
              })}
            </StyledMainGrid>
            <ButtonContainer>
              <Buttons>
                <Button onClick={() => navigate(-1)} type="button">
                  Cancel
                </Button>
                <DeactivateUserDialog
                  trigger={(open) => (
                    <Button
                      onClick={open}
                      type="button"
                      variant="contained"
                      disabled={Boolean(getReportedUsers[0]?.user.deactivated || reportDenied)}
                    >
                      {getReportedUsers[0]?.user.deactivated ? 'Deactivated' : 'Deactivate'}
                    </Button>
                  )}
                  onDeactivate={async (hideArticles) => {
                    await handleDeactivate(getReportedUsers[0]?.user.id, hideArticles);
                  }}
                  loading={deactivateUser.isLoading}
                />
                {!getReportedUsers[0]?.user.deactivated && !reportDenied && (
                  <ConfirmDialog
                    trigger={(open) => (
                      <Button onClick={open} type="button" variant="outlined" disabled={denyReportedUser.isLoading}>
                        Deny Report
                      </Button>
                    )}
                    title="Deny Report"
                    description="Are you sure you want to deny this report? The user will remain active."
                    cancelBtnText="Cancel"
                    submitBtnText="Deny Report"
                    loading={denyReportedUser.isLoading}
                    onConfirm={async (close) => {
                      await handleDenyReport(getReportedUsers[0]?.user.id);
                      close();
                    }}
                  />
                )}
                {getReportedUsers[0]?.user.deactivated && (
                  <ConfirmDialog
                    trigger={(open) => (
                      <Button onClick={open} type="button" variant="contained">
                        Reinstate
                      </Button>
                    )}
                    title="Reinstate Deactivated User"
                    description="Are you sure you want to reinstate this deactivated user?"
                    cancelBtnText={'Cancel'}
                    submitBtnText={'Submit'}
                    onConfirm={async (close) => {
                      await handleReinstate(getReportedUsers[0]?.user.id);
                      close();
                    }}
                  />
                )}
              </Buttons>
            </ButtonContainer>
          </StyledCard>
        </Container>
      )}
    </>
  );
}

const Container = styled('div')({
  padding: '20px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const TopBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
});

const StyledCard = styled(Card)({
  padding: '40px',
});

const StyledMainGrid = styled(Grid)({
  display: 'block',
  maxHeight: '1200px',
  overflow: 'scroll',
  marginTop: '0',
});

const StyledGrid = styled(Grid)({
  borderTop: '1px solid rgba(224, 224, 224, 1)',
  paddingBottom: '32px',
  '&:first-child': {
    borderTop: 0,
  },
});

const ButtonContainer = styled('div')({
  marginTop: '40px',
  alignSelf: 'flex-end',
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-end',
});

const Buttons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  gap: '7px',
  '& > *': {
    margin: '5px',
  },
});

export default ReportedUserEdit;
